import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { scopedMinijobEmployees } from '@app/+authenticated/shared/minijob-payout/minijob-payout-scope';
import { MinijobPayoutApiService } from '@app/+authenticated/shared/minijob-payout/services/minijob-payout-api.service';
import { type MinijobPayoutEmployeeResponse } from '@app/+authenticated/shared/minijob-payout/services/minijob-payout.model';
import { accountModuleStore } from '@app/reducers/account-module/account-module.state';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { getEmployeeTeamDepartments } from '@app/reducers/orm/employee/employee.selectors';
import { Store } from '@ngrx/store';
import { _, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { hasPermissionForAllDepartments } from '@sb/shared/permission/util';
import { type PlainDate } from '@shiftbase-com/ts-shared/utilities';
import { forkJoin, map, take } from 'rxjs';

import { subTextKeyFor } from '../../payroll-progress';
import { PayrollRunService } from '../../payroll-run.service';
import { type PayrollRunScope } from '../../payroll.model';
import {
  calendarMonthsInRange,
  countOutstandingPayouts,
  MINIJOB_STEP_PERMISSION,
  nextOutstandingMonth,
} from './payroll-minijob';

/**
 * Minijob payouts for the payroll run, and the count the Minijob step reports as its backlog. Read
 * as soon as the run has a scope, so the progress panel knows the step's state before the manager
 * reaches it — the same shape as `PayrollBalancesService`.
 *
 * Payouts are booked per calendar month, so a period spanning two months is two requests. The count
 * is withheld (`hasLoaded` false) until every month has answered, because an unloaded answer would
 * otherwise read as a cleared step.
 *
 * Nothing is requested while the module is off, or while the manager does not hold the edit
 * permission in *every* department the run covers: the endpoint answers only for the employees the
 * manager may edit, so a partial grant would read as a false all-clear for the departments it does
 * not cover.
 *
 * A re-read keeps the previous answer on show — dropping to 'not loaded' mid-refresh would flip a
 * complete step back to outstanding and forward again, which `createStepTransitionTracker` reports
 * as the step being reopened.
 */
@Injectable({ providedIn: 'root' })
export class PayrollMinijobService {
  #store = inject<Store<AppState>>(Store);
  #api = inject(MinijobPayoutApiService);
  #run = inject(PayrollRunService);
  #translate = inject(TranslateService);

  #minijobEnabled = this.#store.selectSignal(accountModuleStore.selectMinijobEnabled);
  #permissionState = this.#store.selectSignal(getPermissionState);
  #teamDepartments = this.#store.selectSignal(getEmployeeTeamDepartments);

  // null while no complete answer is in hand: nothing asked yet, a request still out, or a failure.
  #payoutsByMonth = signal<MinijobPayoutEmployeeResponse[][] | null>(null);
  #requestedFor: string | null = null;

  // Bumped whenever a load starts or the state is cleared, so a response for a load the service has
  // since moved on from is recognised as stale rather than letting the slower of two answers win.
  #loadToken = 0;

  months = computed<PlainDate[]>(() => {
    const scope = this.#run.scope();

    return scope ? calendarMonthsInRange(scope.range) : [];
  });

  // `hasPermissionForAllDepartments` treats an empty `departments` array as "unspecified" and falls
  // back to the global `selectedDepartments`, whose `every()` over an empty list answers `true` —
  // hence the length check.
  #canPrepare = computed(() => {
    const scope = this.#run.scope();

    return (
      scope !== null &&
      this.#minijobEnabled() &&
      scope.departmentIds.length > 0 &&
      hasPermissionForAllDepartments(
        { permissions: MINIJOB_STEP_PERMISSION, userId: 'me', departments: scope.departmentIds },
        this.#permissionState(),
      )
    );
  });

  hasLoaded = computed(() => this.#payoutsByMonth() !== null);

  // The endpoint answers account-wide; the run's departments are what the step reports on.
  #scopedPayoutsByMonth = computed<MinijobPayoutEmployeeResponse[][] | null>(() => {
    const payoutsByMonth = this.#payoutsByMonth();
    const scope = this.#run.scope();

    if (!payoutsByMonth || !scope) {
      return null;
    }

    return payoutsByMonth.map((employees) =>
      scopedMinijobEmployees(employees, this.#teamDepartments(), scope.departmentIds),
    );
  });

  outstanding = computed(() => {
    const payoutsByMonth = this.#scopedPayoutsByMonth();

    return payoutsByMonth ? countOutstandingPayouts(payoutsByMonth) : 0;
  });

  subText = computed(() => {
    if (!this.hasLoaded()) {
      return this.#translate.instant(_('Checking Minijob payouts'));
    }

    const count = this.outstanding();
    const key = subTextKeyFor(count, {
      zero: _('No payouts to prepare'),
      one: _('[count] payout to prepare'),
      many: _('[count] payouts to prepare'),
    });

    return this.#translate.instant(key, { count });
  });

  constructor() {
    effect(() => {
      const scope = this.#run.scope();
      const months = this.months();
      const canPrepare = this.#canPrepare();

      untracked(() => this.#loadFor(scope, months, canPrepare));
    });
  }

  /**
   * Re-reads every month after the manager saved payouts from the step. The answer already in hand
   * stays on show until the new one lands — see the class comment.
   */
  refresh(): void {
    const months = this.months();

    if (!this.#run.scope() || !this.#canPrepare() || months.length === 0) {
      return;
    }

    this.#request(months);
  }

  /**
   * Where the step sends the manager after saving `month`: the next month in scope with anyone
   * still to pay, or `null` when the later months are settled (or nothing has loaded). Answered
   * from the payouts in hand, so it is read before `refresh()` replaces them — a save touches only
   * its own month, and the later months' rows are still current.
   */
  nextOutstandingMonthAfter(month: PlainDate): PlainDate | null {
    const payoutsByMonth = this.#scopedPayoutsByMonth();

    return payoutsByMonth ? nextOutstandingMonth(this.months(), payoutsByMonth, month) : null;
  }

  #loadFor(scope: PayrollRunScope | null, months: PlainDate[], canPrepare: boolean): void {
    if (!scope || !canPrepare || months.length === 0) {
      this.#requestedFor = null;
      this.#payoutsByMonth.set(null);
      this.#loadToken++;
      return;
    }

    const requestKey = months.join('|');

    if (requestKey === this.#requestedFor) {
      return;
    }

    // Unlike a refresh, this answer describes months the run no longer covers, so it goes before
    // the new one is asked for rather than lingering as this scope's count.
    this.#payoutsByMonth.set(null);
    this.#request(months);
  }

  #request(months: PlainDate[]): void {
    this.#requestedFor = months.join('|');

    const token = ++this.#loadToken;

    // `employees` is typed non-nullable, but the key is the endpoint's to send: an omitted list
    // counts as nothing outstanding rather than throwing.
    forkJoin(months.map((month) => this.#api.getPayouts(month).pipe(map((response) => response.employees ?? []))))
      .pipe(take(1))
      .subscribe({
        next: (payoutsByMonth) => {
          if (token !== this.#loadToken) {
            return;
          }

          this.#payoutsByMonth.set(payoutsByMonth);
        },
        // The interceptor already toasts; leaving null keeps the step unresolved rather than
        // resolving on an answer nobody has.
        error: () => undefined,
      });
  }
}
